import type { CityData } from "@/lib/cities-data.types";

import { varaNameHi } from "@/lib/vedic-names-hi";

// Relative re-export: the SEO render-locale contract follows only "./" and "../"
// re-exports when it walks a page's render graph.
export { displayPakshaName, displayTithiName } from "../../lib/tithi-names-hi";

export type TithiLanguage = "en" | "hi";

export const TITHI_DEFAULT_CITY: CityData = {
  slug: "new-delhi",
  name: "New Delhi",
  name_hi: "नई दिल्ली",
  state: "Delhi",
  latitude: 28.6139,
  longitude: 77.209,
  timezone: "Asia/Kolkata",
};

export const TITHI_COPY = {
  en: {
    headerTitle: "Aaj Ki Tithi",
    headerSubtitle: "Today's Hindu lunar day",
    cityLabel: "Your city",
    cityPlaceholder: "Search your city",
    cityHelp: "Tithi boundaries and daily timings are calculated for this location.",
    loading: "Calculating today's tithi for your city…",
    refreshing: "Updating the engine result for this city…",
    error: "Today's tithi could not be calculated. Please try again.",
    retry: "Try again",
    patternTitle: "Today's Tithi pattern",
    significanceTitle: "Significance",
    auspiciousForNewWork: "Auspicious for new work",
    auspiciousYes: "Yes — this tithi is favorable for starting new activities",
    auspiciousNo: "No — this tithi is better for worship and spiritual practice",
    snapshotTitle: "Today's Panchang snapshot",
    fullPanchang: "See full Panchang",
    tithi: "Tithi",
    nakshatra: "Nakshatra",
    yoga: "Yoga",
    karana: "Karana",
    vara: "Vara (day)",
    sunrise: "Sunrise",
    purposeTitle: "What are you checking today's timing for?",
    purposeHint: "Choose one purpose to see the relevant engine anchors.",
    previewEyebrow: "Engine-backed preview",
    previewTitle: "Today's timing anchors",
    previewIntro: "These are the Panchang facts calculated for your selected city and purpose.",
    previewBoundary:
      "This is a public daily snapshot, not a personal Muhurat verdict. A personal window also needs your birth chart and the exact purpose.",
    choosePurpose: "Choose one purpose above to build today's preview.",
    previewUpdating: "Recalculating the preview for your new city…",
    notAvailable: "Not returned by today's engine result",
    detected: "Detected by the engine",
    notDetected: "Not detected by the engine",
    brahmaMuhurat: "Brahma Muhurat",
    rahuKaal: "Rahu Kaal",
    sunset: "Sunset",
    moonRashi: "Moon sign",
    abhijitMuhurat: "Abhijit Muhurat",
    amritKaal: "Amrit Kaal",
    sarvarthaSiddhi: "Sarvartha Siddhi Yoga",
  },
  hi: {
    headerTitle: "आज की तिथि",
    headerSubtitle: "आज का हिंदू चंद्र दिवस",
    cityLabel: "आपका शहर",
    cityPlaceholder: "अपना शहर खोजें",
    cityHelp: "तिथि की सीमा और दैनिक समय इसी स्थान के अनुसार निकाले जाते हैं।",
    loading: "आपके शहर के लिए आज की तिथि की गणना हो रही है…",
    refreshing: "इस शहर के लिए इंजन का परिणाम बदला जा रहा है…",
    error: "आज की तिथि की गणना नहीं हो सकी। कृपया फिर प्रयास करें।",
    retry: "फिर प्रयास करें",
    patternTitle: "आज की तिथि का प्रकार",
    significanceTitle: "महत्व",
    auspiciousForNewWork: "नए कार्य के लिए शुभ",
    auspiciousYes: "हाँ — यह तिथि नई गतिविधि शुरू करने के लिए अनुकूल मानी जाती है",
    auspiciousNo: "नहीं — यह तिथि पूजा और आध्यात्मिक साधना के लिए अधिक उपयुक्त मानी जाती है",
    snapshotTitle: "आज के पंचांग का सार",
    fullPanchang: "पूरा पंचांग देखें",
    tithi: "तिथि",
    nakshatra: "नक्षत्र",
    yoga: "योग",
    karana: "करण",
    vara: "वार",
    sunrise: "सूर्योदय",
    purposeTitle: "आप आज का समय किस काम के लिए देख रहे हैं?",
    purposeHint: "आज के संबंधित पंचांग आधार देखने के लिए एक उद्देश्य चुनें।",
    previewEyebrow: "इंजन-आधारित झलक",
    previewTitle: "आज के समय के पंचांग आधार",
    previewIntro: "ये तथ्य आपके चुने हुए शहर और उद्देश्य के लिए पंचांग इंजन ने निकाले हैं।",
    previewBoundary:
      "यह सार्वजनिक दैनिक सार है, व्यक्तिगत मुहूर्त का निर्णय नहीं। व्यक्तिगत समय के लिए आपकी जन्म कुंडली और सटीक उद्देश्य भी आवश्यक हैं।",
    choosePurpose: "आज की झलक बनाने के लिए ऊपर एक उद्देश्य चुनें।",
    previewUpdating: "आपके नए शहर के लिए झलक की दोबारा गणना हो रही है…",
    notAvailable: "आज के इंजन परिणाम में उपलब्ध नहीं",
    detected: "इंजन ने पाया",
    notDetected: "इंजन ने नहीं पाया",
    brahmaMuhurat: "ब्रह्म मुहूर्त",
    rahuKaal: "राहु काल",
    sunset: "सूर्यास्त",
    moonRashi: "चंद्र राशि",
    abhijitMuhurat: "अभिजित मुहूर्त",
    amritKaal: "अमृत काल",
    sarvarthaSiddhi: "सर्वार्थ सिद्धि योग",
  },
} as const;

export function displayVaraName(
  vara: { name: string; name_hi?: string | null },
  language: TithiLanguage
): string {
  if (language !== "hi") return vara.name;
  return varaNameHi(vara.name_hi) ?? varaNameHi(vara.name) ?? vara.name_hi ?? vara.name;
}

export function tithiDateLabel(date: string, language: TithiLanguage): string {
  return new Intl.DateTimeFormat(language === "hi" ? "hi-IN" : "en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(`${date}T12:00:00+05:30`));
}
